import styled from '@emotion/styled'
import { Role } from '@pubstack/common/src/rolesAndFeatures'
import { ScopeUser } from '@pubstack/common/src/user'
import { useAtomValue } from 'jotai'
import { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useUsers } from '~/api/app/user.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import Button from '~/components/Button'
import { Input } from '~/components/Input'
import { Link } from '~/components/Link'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { Tooltip } from '~/components/Tooltip'
import Table, { EmptyTable, handleTableSearchAndSort, onColumnSort, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { SettingsMembersPageAddFlyout } from '~/modules/settings/members/SettingsMembersPageAddFlyout'
import { SettingsMembersPageEditFlyout } from '~/modules/settings/members/SettingsMembersPageEditFlyout'
import { currentScopeState } from '~/state'
import { SUPPORT_MAIL } from '~/utils/constants'
import { SETTINGS_NAV_CONFIG } from '~/utils/settings'
import { SettingsMembersPageDeleteModal } from './SettingsMembersPageDeleteModal'

const SettingsMembersWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SettingsMembersHeader = styled.div`
  display: flex;
  justify-content: space-between;
`

const ActionsTableCell = styled(TableCell)`
  width: 0; /** necessary to get the last cell to shrink down to hug action buttons */
`

const SettingsMembersActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`

export const USER_ROLE_OPTIONS: SelectOptionProp<Role>[] = [
  { label: 'User', value: 'user', trackingLabel: 'common-settings-members-user-set' },
  { label: 'Owner', value: 'owner', trackingLabel: 'common-settings-members-owner-set' },
]

export const SettingsMembersPage = () => {
  const user = useAuthenticatedUser()
  const currentScope = useAtomValue(currentScopeState)
  const { data: users, isLoading, isError } = useUsers()
  const modal = useGlobalModal()
  const flyout = useGlobalFlyout()
  const canEditOrDeleteMember = user.getScopeRole() === 'owner' || user.isAdmin
  const membersCols: TableColumns<ScopeUser> = [
    { name: 'Member', attributeSort: 'nickname', isSortable: true, order: 'ascending' },
    { name: 'E-mail', attributeSort: 'email', isSortable: true, order: 'none' },
    { name: 'Role', attributeSort: 'role', isSortable: true, order: 'none' },
    ...(canEditOrDeleteMember ? ([{ name: 'Action', isSortable: false }] as TableColumns) : []),
  ]
  const [columns, setColumns] = useState<TableColumns<ScopeUser>>(membersCols)

  const { control } = useForm({
    defaultValues: {
      search: '',
    },
  })
  const memberSearch = useWatch({ control, name: 'search' })

  const sortedMembers = handleTableSearchAndSort<ScopeUser>(columns, users, memberSearch, ['nickname', 'email'])

  const validateMemberEmail = (email: string) => {
    return !users.some((member) => member.email.toLocaleLowerCase().trim() === email?.toLocaleLowerCase().trim())
  }

  return (
    <SettingsMembersWrapper>
      <SettingsMembersHeader>
        <Input control={control} name={'search'} labelIsPlaceholder label={'Search'} iconLeft={'search'} />
        {canEditOrDeleteMember && (
          <Tooltip title={'Adding new member is disabled for this organization.'} positions={['left']} disabled={!currentScope.disableCanAddMember}>
            <Button
              iconName={'add_person'}
              onClick={() =>
                flyout.open(
                  SettingsMembersPageAddFlyout,
                  { color: SETTINGS_NAV_CONFIG.members.color, closeTrackingLabel: 'common-settings-members-creation-close' },
                  {
                    validateMember: validateMemberEmail,
                  }
                )
              }
              trackingLabel={'common-settings-members-new'}
              disabled={currentScope.disableCanAddMember}
            >
              Add member
            </Button>
          </Tooltip>
        )}
      </SettingsMembersHeader>
      <Table isLoading={isLoading} columns={columns} onClickHeading={(column) => onColumnSort(columns, column, setColumns)}>
        {sortedMembers.length ? (
          sortedMembers.map((member, index) => (
            <TableRow
              clickable={
                canEditOrDeleteMember
                  ? {
                      onClick: () => {
                        flyout.open(
                          SettingsMembersPageEditFlyout,
                          { color: SETTINGS_NAV_CONFIG.members.color, closeTrackingLabel: 'common-settings-members-edition-close' },
                          {
                            member,
                          }
                        )
                      },
                      trackingLabel: 'common-settings-members-edit',
                    }
                  : undefined
              }
              key={index}
            >
              <TableCell>{member.nickname}</TableCell>
              <TableCell>{member.email}</TableCell>
              <TableCell>{member.role[0].toUpperCase() + member.role.slice(1)}</TableCell>
              {canEditOrDeleteMember && (
                <ActionsTableCell>
                  <SettingsMembersActions>
                    <Tooltip title={'Edit'}>
                      <Button
                        onClick={(event) => {
                          event.stopPropagation()
                          flyout.open(
                            SettingsMembersPageEditFlyout,
                            { color: SETTINGS_NAV_CONFIG.members.color, closeTrackingLabel: 'common-settings-members-edition-close' },
                            {
                              member,
                            }
                          )
                        }}
                        variant={'tertiary'}
                        iconName={'edit'}
                        trackingLabel={'common-settings-members-icon-edit'}
                      />
                    </Tooltip>
                    <Tooltip title={'Remove'}>
                      <Button
                        onClick={(event) => {
                          event.stopPropagation()
                          modal.open(SettingsMembersPageDeleteModal, {
                            member,
                          })
                        }}
                        variant={'tertiary'}
                        iconName={'delete'}
                        trackingLabel={'common-settings-members-delete'}
                      />
                    </Tooltip>
                  </SettingsMembersActions>
                </ActionsTableCell>
              )}
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colspan={columns.length}>
              <EmptyTable>
                {isError ? (
                  <div>
                    <h2>An error occurred when trying to get members of your team</h2>
                    <p>
                      Please refresh the page or contact <Link label={SUPPORT_MAIL} href={`mailto:${SUPPORT_MAIL}`} trackingLabel={'common-mail-to-support-navigate'} /> if the issue persists.
                    </p>
                  </div>
                ) : (
                  <div>
                    <h2>No team members</h2>
                    <p>There are no members in your team.</p>
                    {canEditOrDeleteMember && (
                      <Tooltip title={'Adding new member is disabled for this organization.'} positions={['top']} disabled={!currentScope.disableCanAddMember}>
                        <Button
                          iconName={'add_person'}
                          onClick={() =>
                            flyout.open(
                              SettingsMembersPageAddFlyout,
                              { color: SETTINGS_NAV_CONFIG.members.color, closeTrackingLabel: 'common-settings-members-creation-close' },
                              {
                                validateMember: validateMemberEmail,
                              }
                            )
                          }
                          trackingLabel={'common-settings-members-new'}
                          disabled={currentScope.disableCanAddMember}
                        >
                          Add member
                        </Button>
                      </Tooltip>
                    )}
                  </div>
                )}
              </EmptyTable>
            </TableCell>
          </TableRow>
        )}
      </Table>
    </SettingsMembersWrapper>
  )
}
